import { useAnalytics } from '@app/component/analytics-context';
import { useGetRootViewer } from '@block-pdf/signal/pdfViewer';
import { commentsStore } from '@block-pdf/store/comments/commentStore';
import type { PdfRootLayout } from '@block-pdf/type/comments';
import { useBlockId } from '@core/block';
import { type DeleteCommentInfo, isRoot } from '@core/comments/commentType';
import { threadMeasureContainerId } from '@core/comments/Thread';
import { blockElementSignal } from '@core/signal/blockElement';
import type {
  CreateCommentRequest,
  EditCommentRequest,
} from '@service-storage/generated/schemas';
import type { CreateCommentResponse } from '@service-storage/generated/schemas/createCommentResponse';
import { createCallback } from '@solid-primitives/rootless';
import {
  useAttachHighlightCommentResource,
  useCreateFreeCommentResource,
  useCreateHighlightCommentResource,
  useCreateThreadReplyResource,
  useDeleteCommentResource,
  useEditCommentResource,
} from '../commentsResource';
import { highlightsUuidMap } from '../highlight';
import { newThreadPlaceable, useDeleteNewFreeComment } from './freeComments';
import { useDeleteNewHighlightComment } from './highlightComments';

export function useCreateComment() {
  const analytics = useAnalytics();

  const deleteNewComments = useDeleteNewComments();
  const createFreeComment = useCreateFreeCommentResource();
  const createHighlightComment = useCreateHighlightCommentResource();
  const attachHighlightComment = useAttachHighlightCommentResource();
  const createThreadReply = useCreateThreadReplyResource();

  return createCallback(
    async (info: CreateCommentRequest & { threadId: number }) => {
      analytics.track('comment_create', { blockType: 'pdf' });
      const { threadId, text, mentions } = info;

      // new thread + anchor
      if (threadId === -1) {
        const comment = commentsStore.get.find((c) => c.threadId === threadId);
        if (!comment) {
          console.error('Unable to comment');
          return null;
        }

        let response: CreateCommentResponse | null = null;
        switch (comment.type) {
          case 'highlight':
            const highlight = highlightsUuidMap()?.[comment.anchorId];
            if (!highlight) {
              console.error('Unable to find highlight');
              return response;
            }

            if (highlight.existsOnServer) {
              response = await attachHighlightComment(
                text,
                highlight.uuid,
                mentions
              );
            } else {
              response = await createHighlightComment(
                text,
                highlight,
                mentions
              );
            }
            break;
          case 'free':
            const newThreadPlaceable_ = newThreadPlaceable();
            if (
              !newThreadPlaceable_ ||
              newThreadPlaceable_.internalId !== comment.anchorId
            ) {
              console.error('Unable to find new thread placeable');
              return response;
            }

            response = await createFreeComment(
              text,
              newThreadPlaceable_,
              mentions
            );
            break;
          default:
            console.error('invalid comment type', comment.type);
            return response;
        }

        if (response) {
          deleteNewComments();
        }

        return response;
      }

      return await createThreadReply(info);
    }
  );
}

export function useUpdateComment() {
  const analytics = useAnalytics();

  const editComment = useEditCommentResource();

  return createCallback((commentId: number, info: EditCommentRequest) => {
    analytics.track('comment_update', { blockType: 'pdf' });
    return editComment(commentId, info);
  });
}

export function useDeleteComment() {
  const analytics = useAnalytics();

  const deleteComment = useDeleteCommentResource();
  const deleteNewComments = useDeleteNewComments();

  return createCallback(async (info: DeleteCommentInfo) => {
    const commentId = info.commentId;

    if (commentId === -1) {
      deleteNewComments();
      return false;
    }

    const success = await deleteComment(commentId, {
      removeAnchorThreadOnly: info.removeAnchorThreadOnly,
    });

    if (success) {
      analytics.track('comment_delete', { blockType: 'pdf' });
    }
    return success;
  });
}

export function useDeleteNewComments() {
  const deleteHighlightComment = useDeleteNewHighlightComment();
  const deleteFreeComment = useDeleteNewFreeComment();

  return createCallback(() => {
    deleteHighlightComment();
    deleteFreeComment();
  });
}

export function useScrollToCommentThread() {
  const viewer = useGetRootViewer();
  const comments = commentsStore.get;
  const blockElement = blockElementSignal.get;
  const documentId = useBlockId();

  const scrollIntoView = (el: HTMLElement) => {
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  };

  return async (threadId: number) => {
    const measureContainerId = threadMeasureContainerId(documentId, threadId);
    let measureContainer = document.getElementById(measureContainerId);
    const blockEl = blockElement();
    if (!blockEl) {
      console.error('Unable to find block element');
      return;
    }

    return new Promise<void>((resolve) => {
      const intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 1) {
            setTimeout(() => {
              if (!measureContainer) return;
              scrollIntoView(measureContainer);
            }, 0);
          }
          intersectionObserver.disconnect();
          mutationObserver.disconnect();
          resolve();
        },
        {
          threshold: 1.0, // Ensures the element is fully in view before resolving
        }
      );

      const mutationObserver = new MutationObserver(() => {
        measureContainer = document.getElementById(measureContainerId);
        if (measureContainer) {
          mutationObserver.disconnect();
          intersectionObserver.observe(measureContainer);
        }
      });

      // If the element is already in the DOM, start observing it immediately
      // otherwise, listen for it to be added to the DOM
      if (measureContainer) {
        intersectionObserver.observe(measureContainer);
        scrollIntoView(measureContainer);
      } else {
        mutationObserver.observe(blockEl, {
          childList: true,
          subtree: true,
        });

        // since page overlays are only rendered in viewport
        // we need to force a render by scrolling to the page
        setTimeout(() => {
          const viewer_ = viewer();
          if (!viewer_) return;

          const rootComment = comments
            .filter(isRoot)
            .find((c) => c.threadId === threadId) as PdfRootLayout | undefined;
          if (!rootComment) return;

          // if the comment is already in the viewport, we don't need to scroll
          if (measureContainer) return;

          mutationObserver.disconnect();
          intersectionObserver.disconnect();

          viewer_.scrollTo({
            pageNumber: rootComment.layout.pageIndex + 1,
          });

          measureContainer = document.getElementById(measureContainerId);
          if (measureContainer) {
            scrollIntoView(measureContainer);
            intersectionObserver.observe(measureContainer);
          } else {
            mutationObserver.observe(blockEl, {
              childList: true,
              subtree: true,
            });
          }
        }, 250);
      }

      // automatically clean up after a timeout period
      setTimeout(() => {
        intersectionObserver.disconnect();
        mutationObserver.disconnect();
        resolve();
      }, 2000);
    });
  };
}
